import type { FC, ReactNode } from 'react'
import type { KridantaForm, KridantaId } from '@/sanskrit/lib/sanskrit/derivation'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { ArrowRight, Equal, Plus } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { Chip, PageHeader, Segmented, StatusBadge } from '@/sanskrit/components/vani/bits'
import { useT } from '@/sanskrit/components/providers/app-state'
import { getDhatu, padasOf } from '@/sanskrit/lib/sanskrit/dhatus'
import { getParadigm } from '@/sanskrit/lib/sanskrit/paradigms'
import { VACANAS } from '@/sanskrit/lib/sanskrit/types'
import { GENDER_LABEL, VACANA_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import {
    KRIDANTA_FORMS,
    KRIDANTA_PRATYAYAS,
    TADDHITA_EXAMPLES,
    UPASARGAS,
    UPASARGA_EXAMPLES,
    UPASARGA_VERSE
} from '@/sanskrit/lib/sanskrit/derivation'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'

type Tab = 'krt' | 'taddhita' | 'upasarga'

const MATRIX_PRATYAYAS: KridantaId[] = ['kta', 'ktavatu', 'shatr', 'tumun', 'ktva', 'tavyat', 'aniyar']

/** Prefer the masculine form for the grid, then neuter, then any. */
const primaryForm = (forms: KridantaForm[]): KridantaForm | undefined => forms.find(f => f.gender === 'm') ?? forms.find(f => f.gender === 'n') ?? forms[0]

function KrtTab() {
    const t = useT()
    const [pratyaya, setPratyaya] = useState<KridantaId>('ktva')
    const [selected, setSelected] = useState<{ dhatuId: string; pratyaya: KridantaId } | null>({ dhatuId: 'path', pratyaya: 'ktva' })
    const info = KRIDANTA_PRATYAYAS.find(p => p.id === pratyaya) ?? KRIDANTA_PRATYAYAS[0]

    const roots = useMemo(() => {
        const ids = Array.from(new Set(KRIDANTA_FORMS.map(f => f.dhatuId)))
        return ids
            .map(id => ({ id, count: MATRIX_PRATYAYAS.filter(p => KRIDANTA_FORMS.some(f => f.dhatuId === id && f.pratyaya === p)).length }))
            .filter(r => r.count >= 4 && getDhatu(r.id))
            .map(r => r.id)
    }, [])

    const selectedForms = selected ? KRIDANTA_FORMS.filter(f => f.dhatuId === selected.dhatuId && f.pratyaya === selected.pratyaya) : []
    const selectedInfo = selected ? KRIDANTA_PRATYAYAS.find(p => p.id === selected.pratyaya) : undefined
    const formsOfPratyaya = KRIDANTA_FORMS.filter(f => f.pratyaya === pratyaya)

    const pickCell = (dhatuId: string, p: KridantaId) => {
        setSelected({ dhatuId, pratyaya: p })
        setPratyaya(p)
    }

    return (
        <div className='space-y-6'>
            <div className='no-scrollbar-x -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0'>
                {KRIDANTA_PRATYAYAS.map(p => (
                    <button
                        key={p.id}
                        type='button'
                        onClick={() => setPratyaya(p.id)}
                        aria-pressed={pratyaya === p.id}
                        className={cn('shrink-0 rounded-2xl border-2 px-4 py-2 text-left transition-colors', pratyaya === p.id ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-card hover:border-saffron')}
                    >
                        <span lang='sa' className='deva block text-lg font-semibold'>{p.name}</span>
                        <span className={cn('block text-xs', pratyaya === p.id ? 'text-primary-foreground/75' : 'text-muted-foreground')}>{t(p.meaning)}</span>
                    </button>
                ))}
            </div>

            <motion.section key={info.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className='rounded-[2rem] border border-border bg-card p-5 shadow-sm sm:p-7'>
                <div className='flex flex-wrap items-baseline gap-3'>
                    <p lang='sa' className='literary text-4xl text-ink'>{info.name}</p>
                    <span className='text-sm text-muted-foreground'>{info.iast} · {t(info.kind)}</span>
                    <Chip tone={info.declinable ? 'saffron' : 'ink'}>
                        {info.declinable ? t({ hi: 'रूप बदलता है (विशेषण जैसा)', en: 'Declines like an adjective' }) : t({ hi: 'अव्यय: रूप नहीं बदलता', en: 'Indeclinable: never changes' })}
                    </Chip>
                </div>
                <p className='mt-2 text-lg font-semibold'>{t(info.meaning)}</p>
                <p className='mt-1 text-muted-foreground'>{t(info.usage)}</p>
                <div className='mt-4 flex flex-wrap items-start justify-between gap-2 rounded-2xl bg-saffron-soft/70 p-4'>
                    <div>
                        <Skt text={info.example.sa} className='text-2xl font-semibold text-ink' />
                        <p className='text-sm text-muted-foreground'>{t({ hi: info.example.hi, en: info.example.en })}</p>
                    </div>
                    <AudioButton text={info.example.sa} size='sm' />
                </div>
                <div className='mt-4 flex flex-wrap gap-2'>
                    {formsOfPratyaya.map(f => (
                        <span key={`${f.form}-${f.dhatuId}`} className='rounded-full border border-border bg-background px-3 py-1'>
                            <Skt text={f.form} className='text-base font-semibold' />
                            <span className='ml-1.5 text-xs text-muted-foreground'>{getDhatu(f.dhatuId)?.root}</span>
                        </span>
                    ))}
                </div>
            </motion.section>

            <section>
                <h2 className='font-heading text-2xl'>{t({ hi: 'एक धातु, कई प्रत्यय', en: 'One root, many suffixes' })}</h2>
                <p className='mt-1 text-sm text-muted-foreground'>{t({ hi: 'किसी खाने को छुइए: वह रूप, उसका अर्थ और नियम नीचे दिखेगा।', en: 'Tap a cell to see that form, its meaning and the rule behind it.' })}</p>
                <div className='no-scrollbar-x mt-3 overflow-x-auto rounded-[1.5rem] border border-border bg-card'>
                    <table className='w-full min-w-[46rem] text-left'>
                        <thead>
                            <tr className='border-b border-border bg-muted/50'>
                                <th className='px-3 py-2 text-xs font-semibold text-muted-foreground'>{t({ hi: 'धातु', en: 'Root' })}</th>
                                {MATRIX_PRATYAYAS.map(p => (
                                    <th key={p} className={cn('px-3 py-2', p === pratyaya && 'bg-saffron-soft')}>
                                        <span lang='sa' className='deva text-sm font-semibold'>{KRIDANTA_PRATYAYAS.find(x => x.id === p)?.name}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {roots.map(id => {
                                const d = getDhatu(id)!

                                return (
                                    <tr key={id} className='border-b border-border last:border-0'>
                                        <th className='px-3 py-2'>
                                            <span lang='sa' className='deva block text-base font-semibold'>{d.root}</span>
                                            <span className='block text-xs font-normal text-muted-foreground'>{t(d.meaning)}</span>
                                        </th>
                                        {MATRIX_PRATYAYAS.map(p => {
                                            const form = primaryForm(KRIDANTA_FORMS.filter(f => f.dhatuId === id && f.pratyaya === p))
                                            const active = selected?.dhatuId === id && selected.pratyaya === p

                                            return (
                                                <td key={p} className={cn('px-1.5 py-1.5', p === pratyaya && 'bg-saffron-soft/50')}>
                                                    {form
                                                        ? (
                                                            <button type='button' onClick={() => pickCell(id, p)} aria-pressed={active} className={cn('w-full rounded-lg px-2 py-1.5 text-left transition-colors', active ? 'bg-ink text-primary-foreground' : 'hover:bg-muted')}>
                                                                <span lang='sa' className='deva text-base font-semibold'>{form.form}</span>
                                                            </button>
                                                        )
                                                        : <span className='block px-2 text-muted-foreground'>·</span>}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                {selected && selectedForms.length > 0 && selectedInfo && (
                    <motion.div key={`${selected.dhatuId}-${selected.pratyaya}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className='mt-4 rounded-2xl border border-border bg-card p-5'>
                        <p className='text-sm font-semibold text-muted-foreground'>
                            <span lang='sa' className='deva'>{getDhatu(selected.dhatuId)?.root} + {selectedInfo.name}</span>
                        </p>
                        <div className='mt-2 grid gap-3 sm:grid-cols-2'>
                            {selectedForms.map(f => (
                                <div key={f.form} className='rounded-xl bg-muted/50 p-3'>
                                    <div className='flex items-center justify-between gap-2'>
                                        <Skt text={f.form} className='text-2xl font-semibold text-ink' />
                                        <AudioButton text={f.form} size='sm' />
                                    </div>
                                    <p className='text-sm'>{t(f.meaning)}{f.gender && <span className='text-muted-foreground'> · {t({ hi: GENDER_LABEL[f.gender].hi, en: GENDER_LABEL[f.gender].en })}</span>}</p>
                                    {f.variants && f.variants.length > 0 && <p className='mt-1 text-xs'>{t({ hi: 'दूसरा स्वीकृत रूप:', en: 'Also accepted:' })} <span lang='sa' className='deva font-semibold'>{f.variants.join(', ')}</span></p>}
                                    {f.note && <p className='mt-1 text-xs text-muted-foreground'>{t(f.note)}</p>}
                                    <StatusBadge status={f.status} className='mt-2' />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </section>
        </div>
    )
}

function TaddhitaTab() {
    const t = useT()
    const groups = useMemo(() => {
        const map = new Map<string, typeof TADDHITA_EXAMPLES>()
        for (const ex of TADDHITA_EXAMPLES) map.set(ex.pratyaya, [...(map.get(ex.pratyaya) ?? []), ex])
        return Array.from(map.entries())
    }, [])

    return (
        <div className='space-y-6'>
            <p className='max-w-3xl text-muted-foreground'>
                {t({
                    hi: 'कृत् प्रत्यय धातु से शब्द बनाते हैं; तद्धित प्रत्यय पहले से बने शब्द (संज्ञा, विशेषण) से नया शब्द बनाते हैं। कई तद्धित प्रत्ययों में पहले स्वर की वृद्धि होती है: अ → आ, इ/ए → ऐ, उ/ओ → औ।',
                    en: 'Kṛt suffixes make words from roots; taddhita suffixes make new words from existing nouns and adjectives. Many taddhita suffixes strengthen the first vowel (vṛddhi): अ → आ, इ/ए → ऐ, उ/ओ → औ.'
                })}
            </p>
            {groups.map(([pratyaya, items]) => (
                <section key={pratyaya}>
                    <h2 className='mb-2 flex items-baseline gap-2 font-heading text-xl'>
                        <span lang='sa' className='deva text-2xl text-ink'>{pratyaya}</span>
                        <span className='text-sm font-normal text-muted-foreground'>{t({ hi: `${items.length} उदाहरण`, en: `${items.length} example${items.length === 1 ? '' : 's'}` })}</span>
                    </h2>
                    <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {items.map(ex => (
                            <div key={ex.result} className='rounded-[1.25rem] border border-border bg-card p-4'>
                                <p className='flex flex-wrap items-center gap-2 text-xl font-semibold'>
                                    <span lang='sa' className='deva'>{ex.base}</span>
                                    <Plus className='size-4 text-muted-foreground' aria-hidden='true' />
                                    <span lang='sa' className='deva text-saffron-strong'>{ex.pratyaya}</span>
                                    <Equal className='size-4 text-muted-foreground' aria-hidden='true' />
                                    <span lang='sa' className='deva text-terracotta'>{ex.result}</span>
                                </p>
                                <p className='mt-1 text-sm'>{t(ex.meaning)}</p>
                                <p className='mt-1 text-xs text-muted-foreground'>{t(ex.note)}</p>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}

function UpasargaTab() {
    const t = useT()
    const bases = useMemo(() => Array.from(new Set(UPASARGA_EXAMPLES.map(e => e.baseDhatuId))).filter(id => getDhatu(id)), [])
    const [baseId, setBaseId] = useState('gam')
    const options = UPASARGA_EXAMPLES.filter(e => e.baseDhatuId === baseId && getDhatu(e.dhatuId))
    const [choice, setChoice] = useState<string>('agam')
    const active = options.find(o => o.dhatuId === choice) ?? options[0]
    const base = getDhatu(baseId)
    const derived = active ? getDhatu(active.dhatuId) : undefined
    const pada = derived ? padasOf(derived)[0] : 'P'
    const paradigm = derived ? getParadigm(derived.id, 'lat', pada) : null

    const pickBase = (id: string) => {
        setBaseId(id)
        const first = UPASARGA_EXAMPLES.find(e => e.baseDhatuId === id)
        if (first) setChoice(first.dhatuId)
    }

    return (
        <div className='space-y-6'>
            <section className='rounded-[2rem] border border-border bg-card p-5 shadow-sm sm:p-7'>
                <h2 className='font-heading text-2xl'>{t({ hi: 'उपसर्ग + धातु', en: 'Prefix + root builder' })}</h2>
                <p className='mt-1 text-sm text-muted-foreground'>{t({ hi: 'एक धातु चुनिए, फिर उपसर्ग। देखिए अर्थ कैसे बदलता है।', en: 'Choose a root, then a prefix, and watch the meaning change.' })}</p>

                <div className='mt-4 flex flex-wrap gap-2'>
                    {bases.map(id => {
                        const d = getDhatu(id)!

                        return (
                            <button key={id} type='button' onClick={() => pickBase(id)} aria-pressed={baseId === id} className={cn('rounded-2xl border-2 px-3 py-1.5 transition-colors', baseId === id ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-background hover:border-saffron')}>
                                <span lang='sa' className='deva text-lg font-semibold'>{d.root}</span>
                                <span className={cn('ml-2 text-xs', baseId === id ? 'text-primary-foreground/75' : 'text-muted-foreground')}>{t(d.meaning)}</span>
                            </button>
                        )
                    })}
                </div>

                <div className='mt-3 flex flex-wrap gap-2'>
                    {options.map(o => (
                        <button key={o.dhatuId} type='button' onClick={() => setChoice(o.dhatuId)} aria-pressed={active?.dhatuId === o.dhatuId} className={cn('rounded-full border-2 px-4 py-1.5 transition-colors', active?.dhatuId === o.dhatuId ? 'border-saffron-strong bg-saffron text-ink' : 'border-border bg-card hover:border-saffron')}>
                            <span lang='sa' className='deva text-lg font-semibold'>{o.upasarga}</span>
                        </button>
                    ))}
                </div>

                {base && active && derived && (
                    <motion.div key={derived.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className='mt-6 grid gap-4 lg:grid-cols-[1.2fr_1fr]'>
                        <div className='rounded-2xl bg-saffron-soft/70 p-5'>
                            <div className='flex flex-wrap items-center gap-3 text-2xl font-semibold sm:text-3xl'>
                                <span lang='sa' className='deva rounded-xl bg-saffron px-3 py-1 text-ink'>{active.upasarga}</span>
                                <Plus className='size-5 text-muted-foreground' aria-hidden='true' />
                                <span lang='sa' className='deva'>{base.citation}</span>
                                <ArrowRight className='size-5 text-muted-foreground' aria-hidden='true' />
                                <Skt text={derived.citation} className='text-terracotta' />
                                <AudioButton text={derived.citation} size='sm' />
                            </div>
                            <p className='mt-3'>
                                <span className='text-muted-foreground'>{t(base.meaning)}</span>
                                <ArrowRight className='mx-2 inline size-4 text-muted-foreground' aria-hidden='true' />
                                <span className='font-semibold'>{t(active.meaning)}</span>
                            </p>
                            {derived.notes.map((n, i) => <p key={i} className='mt-2 text-sm'>{t(n)}</p>)}
                            {derived.examples[0] && (
                                <div className='mt-3 rounded-xl bg-card/80 p-3'>
                                    <Skt text={derived.examples[0].sa} className='text-lg font-semibold' />
                                    <p className='text-sm text-muted-foreground'>{t({ hi: derived.examples[0].hi, en: derived.examples[0].en })}</p>
                                </div>
                            )}
                        </div>
                        {paradigm && (
                            <div className='rounded-2xl border border-border bg-background p-5'>
                                <p className='text-sm font-semibold'>{t({ hi: 'लट् लकार, प्रथम पुरुष', en: 'Present tense, प्रथम पुरुष' })}</p>
                                <div className='mt-2 grid grid-cols-3 gap-2'>
                                    {VACANAS.map(v => (
                                        <div key={v} className='rounded-xl bg-muted/60 p-2 text-center'>
                                            <p lang='sa' className='deva text-xs text-muted-foreground'>{VACANA_LABEL[v].short}</p>
                                            <Skt text={paradigm.forms.prathama[v][0]} className='text-base font-semibold' />
                                        </div>
                                    ))}
                                </div>
                                <Link href={`/sanskrit/grammar/dhatu/${derived.id}`} className='mt-4 inline-flex items-center gap-1 text-sm font-semibold text-saffron-strong'>
                                    {t({ hi: 'सभी रूप देखें', en: 'See every form' })}
                                    <ArrowRight className='size-4' />
                                </Link>
                            </div>
                        )}
                    </motion.div>
                )}
            </section>

            <section className='rounded-[2rem] bg-ink p-6 text-primary-foreground sm:p-8'>
                <p lang='sa' className='literary whitespace-pre-line text-2xl leading-relaxed sm:text-3xl'>{UPASARGA_VERSE.sa}</p>
                <p className='mt-3 text-primary-foreground/80'>{t({ hi: UPASARGA_VERSE.hi, en: UPASARGA_VERSE.en })}</p>
                <div className='mt-4'>
                    <AudioButton text={UPASARGA_VERSE.sa.replace('\n', ' ')} variant='pill' label={t({ hi: 'श्लोक सुनें', en: 'Hear the verse' })} />
                </div>
            </section>

            <section>
                <h2 className='font-heading text-2xl'>{t({ hi: 'बाईस उपसर्ग', en: 'The twenty-two prefixes' })}</h2>
                <p className='mt-1 text-sm text-muted-foreground'>{t({ hi: 'प्र, परा, अप, सम्... ये क्रिया से पहले जुड़कर उसका अर्थ बदलते हैं।', en: 'प्र, परा, अप, सम्… attach before a verb and shift its meaning.' })}</p>
                <div className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                    {UPASARGAS.map(u => (
                        <div key={u.sa} className='rounded-xl border border-border bg-card px-3 py-2'>
                            <p className='flex items-baseline justify-between gap-2'>
                                <span lang='sa' className='deva text-xl font-semibold text-ink'>{u.sa}</span>
                                <span lang='sa-Latn' className='iast text-xs text-muted-foreground'>{u.iast}</span>
                            </p>
                            <p className='text-sm text-muted-foreground'>{t(u.sense)}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    )
}

const PratyayaPage: FC = (): ReactNode => {
    const t = useT()
    const [tab, setTab] = useState<Tab>('krt')

    return (
        <Layout>
            <Seo title='Suffixes and prefixes' description='Sanskrit kṛt suffixes (क्त, शतृ, तुमुन्, क्त्वा, ल्यप्…), taddhita suffixes and the 22 upasargas, with a root by suffix grid and a prefix builder.' />

            <SanskritGate what='The suffixes page'>
            <PageHeader
                sa='प्रत्ययाः उपसर्गाश्च'
                title={{ hi: 'प्रत्यय और उपसर्ग', en: 'Suffixes and prefixes' }}
                subtitle={{
                    hi: 'एक ही धातु से "पढ़कर", "पढ़ने के लिए", "पढ़ा गया", "पढ़ता हुआ" जैसे दर्जनों शब्द बनते हैं। उपसर्ग धातु का अर्थ ही बदल देते हैं।',
                    en: 'One root gives dozens of words: "having read", "to read", "was read", "while reading". Prefixes change what the root means altogether.'
                }}
            >
                <div className='mt-5'>
                    <Segmented
                        label={t({ hi: 'विभाग', en: 'Section' })}
                        value={tab}
                        onChange={setTab}
                        options={[
                            { value: 'krt', label: t({ hi: 'कृदन्त (धातु से)', en: 'Kṛdanta (from roots)' }) },
                            { value: 'taddhita', label: t({ hi: 'तद्धित (शब्द से)', en: 'Taddhita (from words)' }) },
                            { value: 'upasarga', label: t({ hi: 'उपसर्ग', en: 'Prefixes' }) }
                        ]}
                    />
                </div>
            </PageHeader>

            <div className='mx-auto max-w-6xl space-y-8 px-4 pb-16 sm:px-6'>
                {tab === 'krt' && <KrtTab />}
                {tab === 'taddhita' && <TaddhitaTab />}
                {tab === 'upasarga' && <UpasargaTab />}
            </div>
            </SanskritGate>
        </Layout>
    )
}

export default PratyayaPage
